import React, { useLayoutEffect, useMemo, useRef, useState } from "react";

import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import {
  Box,
  Button,
  Checkbox,
  IconButton,
  InputAdornment,
  MenuItem,
  Stack,
  TextField,
  Typography
} from "@mui/material";
import { DatePicker } from "components/deal/DatePicker/DatePicker";
import { NumberInput } from "components/forms";
import dayjs from "dayjs";
import { useEscapeKey } from "hooks";
import {
  AllocationPreviewRow,
  CurrencyOption,
  PlanDealInvestor,
  PlanDealResult,
  PlannedDealRequest,
  PolicyAssetClass
} from "store/types";

import {
  COLOR_BG_HOVER_SUBTLE,
  COLOR_DISABLED,
  COLOR_ERROR,
  COLOR_PRIMARY,
  COLOR_SECONDARY,
  COLOR_STROKE_DIVIDER,
  COLOR_STROKE_IDLE
} from "../colors";
import { acKey } from "../Target-policy-prompt/useTargetPolicyState";

const ALLOCATION_EPSILON = 0.01;

const planDealResultToRequest = (data: PlanDealResult): PlannedDealRequest => ({
  name: data.name,
  asset_class_id: Number(data.assetClassId),
  asset_class_type: data.assetClassType,
  // Custom asset classes are flat — only send a sub-asset class for system classes.
  ...(data.assetClassType === "system" && data.subAssetClassId != null
    ? { sub_asset_class_id: Number(data.subAssetClassId) }
    : {}),
  amount: data.investmentAmount,
  currency: data.currency,
  expected_date: data.expectedDate,
  investors: data.allocations.map((a) => ({ investor_id: Number(a.id), allocated_amount: a.amount }))
});

const moneyFormat = (value: number, currency: string) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency, maximumFractionDigits: 2 }).format(value);

interface Props {
  mode?: "create" | "edit";
  initial?: PlanDealResult;
  // System + custom asset classes (ids can collide, so each carries its `type`).
  assetClasses: PolicyAssetClass[];
  currencies: CurrencyOption[];
  baseCurrency: string;
  investors: PlanDealInvestor[];
  previewAllocations: (amount: number, currency: string, investorIds: number[]) => Promise<AllocationPreviewRow[]>;
  saveDeal: (data: PlannedDealRequest) => Promise<void>;
  onSubmit: (result: PlanDealResult) => void;
  onCancel: () => void;
}

export const PlanDealPrompt: React.FC<Props> = ({
  mode = "create",
  initial,
  assetClasses,
  currencies,
  baseCurrency,
  investors,
  previewAllocations,
  saveDeal,
  onSubmit,
  onCancel
}) => {
  const isEdit = mode === "edit";

  // Step 1
  const [step, setStep] = useState<1 | 2>(1);
  const [name, setName] = useState(initial?.name ?? "");
  // The asset class is selected by a composite `<type>:<id>` key because system and
  // custom asset-class ids can collide.
  const [assetClassKey, setAssetClassKey] = useState<string>(
    initial?.assetClassId !== undefined && initial.assetClassId !== "" && initial.assetClassType
      ? acKey(initial.assetClassType, initial.assetClassId)
      : ""
  );
  const [subAssetClassId, setSubAssetClassId] = useState<string>(
    initial?.subAssetClassId !== undefined ? String(initial.subAssetClassId) : ""
  );
  const [investmentAmountStr, setInvestmentAmountStr] = useState(
    initial?.investmentAmount ? String(initial.investmentAmount) : ""
  );
  const [investmentAmount, setInvestmentAmount] = useState<number>(initial?.investmentAmount ?? 0);
  const [currency, setCurrency] = useState(initial?.currency ?? baseCurrency);
  const [expectedDate, setExpectedDate] = useState(initial?.expectedDate ?? "");

  // Step 2 — per-investor allocations (a key present in the map = selected investor).
  const [search, setSearch] = useState("");
  const investorIdSet = useMemo(() => new Set(investors.map((i) => String(i.id))), [investors]);
  const [allocations, setAllocations] = useState<Record<string, string>>(() => {
    const seed: Record<string, string> = {};
    (initial?.allocations ?? []).forEach((a) => {
      // Drop allocations for investors no longer in the account (dangling references).
      if (investorIdSet.has(String(a.id))) seed[String(a.id)] = String(a.amount);
    });
    return seed;
  });
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);

  const listRef = useRef<HTMLDivElement>(null);
  const savedScrollTop = useRef(0);

  // Common
  const [isSaving, setIsSaving] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);

  const selectedAssetClass = useMemo(
    () => assetClasses.find((c) => acKey(c.type, c.id) === assetClassKey),
    [assetClasses, assetClassKey]
  );

  const isStep1Valid =
    name.trim().length > 0 && !!assetClassKey && investmentAmount > 0 && !!expectedDate && !!currency;

  const filteredInvestors = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (q) return investors.filter((i) => i.name.toLowerCase().includes(q));
    const isSelected = (inv: PlanDealInvestor) => String(inv.id) in allocations;
    return [...investors.filter(isSelected), ...investors.filter((i) => !isSelected(i))];
  }, [investors, allocations, search]);

  const toggleInvestor = (id: PlanDealInvestor["id"]) => {
    savedScrollTop.current = listRef.current?.scrollTop ?? 0;
    setAllocations((prev) => {
      const key = String(id);
      const next = { ...prev };
      if (key in next) delete next[key];
      else next[key] = "";
      return next;
    });
  };

  const setAllocation = (id: PlanDealInvestor["id"], value: string) =>
    setAllocations((prev) => ({ ...prev, [String(id)]: value }));

  const selectionSignature = Object.keys(allocations).sort().join("|");
  useLayoutEffect(() => {
    if (listRef.current) listRef.current.scrollTop = savedScrollTop.current;
  }, [selectionSignature]);

  const selectedCount = Object.keys(allocations).length;
  const allocatedTotal = Object.values(allocations).reduce((s, v) => s + (Number(v) || 0), 0);
  // Empty selection is allowed (a planned deal with no allocations). Otherwise the
  // per-investor amounts must sum to the deal amount (the backend rejects mismatches).
  const allocationsValid = selectedCount === 0 || Math.abs(allocatedTotal - investmentAmount) <= ALLOCATION_EPSILON;

  const handleSplitProRata = async () => {
    const ids = Object.keys(allocations).map(Number);
    if (ids.length === 0 || investmentAmount <= 0) return;
    setIsPreviewLoading(true);
    setApiError(null);
    try {
      const rows = await previewAllocations(investmentAmount, currency, ids);
      setAllocations((prev) => {
        const next = { ...prev };
        rows.forEach((r) => {
          next[String(r.investor_id)] = String(r.suggested_amount);
        });
        return next;
      });
    } catch (e) {
      setApiError(e instanceof Error && e.message ? e.message : "Could not suggest a split. Try again.");
    } finally {
      setIsPreviewLoading(false);
    }
  };

  useEscapeKey(onCancel);

  const handleNext = () => {
    if (isStep1Valid) setStep(2);
  };
  const handleBack = () => {
    setApiError(null);
    setStep(1);
  };

  const handleSave = async () => {
    setIsSaving(true);
    setApiError(null);
    try {
      const assetClassType = selectedAssetClass?.type ?? "system";
      const result: PlanDealResult = {
        name: name.trim(),
        assetClassId: selectedAssetClass?.id ?? "",
        assetClassType,
        // Custom asset classes have no sub-classes.
        subAssetClassId: assetClassType === "system" ? subAssetClassId || undefined : undefined,
        investmentAmount,
        currency,
        expectedDate,
        allocations: Object.entries(allocations).map(([id, amount]) => ({ id, amount: Number(amount) || 0 }))
      };
      await saveDeal(planDealResultToRequest(result));
      onSubmit(result);
    } catch (e) {
      setApiError(e instanceof Error && e.message ? e.message : "Could not save planned deal. Try again.");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Stack
      sx={{
        position: "absolute",
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%)",
        outline: "none",
        width: 720,
        maxHeight: "calc(100vh - 64px)",
        bgcolor: "white",
        borderRadius: "8px",
        boxShadow:
          "0px 2px 4px -1px rgba(0, 0, 0, 0.20), 0px 4px 5px 0px rgba(0, 0, 0, 0.14), 0px 1px 10px 0px rgba(0, 0, 0, 0.12)",
        overflow: "hidden"
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ px: "24px", py: "16px", borderBottom: `1px solid ${COLOR_STROKE_DIVIDER}` }}
      >
        <Stack>
          <Typography sx={{ color: COLOR_PRIMARY, fontSize: "18px", lineHeight: "24px", fontWeight: 500 }}>
            {isEdit ? "Edit Planned Deal" : "Create Planned Deal"}
          </Typography>
          <Typography sx={{ color: COLOR_SECONDARY, fontSize: "13px", lineHeight: "16px", mt: "2px" }}>
            Step {step} of 2 — {step === 1 ? "Deal details" : "Investor allocation"}
          </Typography>
        </Stack>
        <IconButton onClick={onCancel} sx={{ padding: "4px", color: COLOR_PRIMARY }}>
          <CloseIcon sx={{ fontSize: 20 }} />
        </IconButton>
      </Stack>

      <Stack sx={{ p: "20px 24px", gap: "16px", flex: "1 1 auto", minHeight: 0, overflowY: "auto" }}>
        {step === 1 ? (
          <Stack gap="14px">
            <TextField label="Deal name" value={name} onChange={(e) => setName(e.target.value)} autoFocus fullWidth />
            <Stack direction="row" gap="12px">
              <TextField
                select
                label="Asset class"
                value={assetClassKey}
                onChange={(e) => {
                  setAssetClassKey(e.target.value);
                  setSubAssetClassId("");
                }}
                fullWidth
              >
                {assetClasses.map((c) => (
                  <MenuItem key={acKey(c.type, c.id)} value={acKey(c.type, c.id)}>
                    {c.name}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select
                label="Sub-asset class"
                value={subAssetClassId}
                onChange={(e) => setSubAssetClassId(e.target.value)}
                disabled={!selectedAssetClass?.subClasses?.length}
                fullWidth
              >
                <MenuItem value="">—</MenuItem>
                {selectedAssetClass?.subClasses?.map((s) => (
                  <MenuItem key={s.id} value={String(s.id)}>
                    {s.name}
                  </MenuItem>
                ))}
              </TextField>
            </Stack>
            <Stack direction="row" gap="12px">
              <NumberInput
                label="Investment amount"
                name="investment-amount"
                value={investmentAmountStr}
                onChange={(e) => setInvestmentAmountStr((e.target as HTMLInputElement).value)}
                onBlur={() => {
                  const n = Number(investmentAmountStr);
                  if (Number.isFinite(n) && n > 0) {
                    setInvestmentAmount(n);
                    setInvestmentAmountStr(String(n));
                  } else {
                    setInvestmentAmountStr(investmentAmount > 0 ? String(investmentAmount) : "");
                  }
                }}
                numberInputProps={{ allowNegative: false }}
                fullWidth
              />
              <TextField
                select
                label="Currency"
                value={currency}
                onChange={(e) => setCurrency(e.target.value)}
                sx={{ width: 160, flexShrink: 0 }}
              >
                {currencies.map((c) => (
                  <MenuItem key={c.code} value={c.code}>
                    {c.label}
                  </MenuItem>
                ))}
              </TextField>
            </Stack>
            <DatePicker
              label="Expected date"
              defaultValue={expectedDate}
              onChange={(value) => setExpectedDate(value ? dayjs(value).format("YYYY-MM-DD") : "")}
              onClear={() => setExpectedDate("")}
            />
          </Stack>
        ) : (
          <Stack gap="12px">
            <Stack direction="row" alignItems="center" justifyContent="space-between" gap="12px">
              <Typography sx={{ color: COLOR_PRIMARY, fontSize: "14px", lineHeight: "20px", fontWeight: 500 }}>
                Investors
              </Typography>
              <Typography sx={{ color: COLOR_SECONDARY, fontSize: "13px", lineHeight: "16px" }}>
                {selectedCount} selected
              </Typography>
            </Stack>

            <Stack direction="row" alignItems="center" justifyContent="space-between" gap="12px">
              <Button
                variant="outlined"
                onClick={handleSplitProRata}
                disabled={selectedCount === 0 || investmentAmount <= 0 || isPreviewLoading}
                sx={{ textTransform: "none", color: COLOR_PRIMARY, borderColor: COLOR_STROKE_IDLE }}
              >
                {isPreviewLoading ? "Splitting…" : "Split pro-rata by NAV"}
              </Button>
              <Typography
                sx={{
                  fontSize: "13px",
                  lineHeight: "18px",
                  fontWeight: 500,
                  color: allocationsValid ? COLOR_SECONDARY : COLOR_ERROR,
                  textAlign: "right"
                }}
              >
                Allocated {moneyFormat(allocatedTotal, currency)} / {moneyFormat(investmentAmount, currency)}
              </Typography>
            </Stack>

            <TextField
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name"
              size="small"
              fullWidth
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: 18, color: COLOR_SECONDARY }} />
                  </InputAdornment>
                ),
                sx: { borderRadius: "8px" }
              }}
            />

            <Stack
              ref={listRef}
              sx={{
                border: `1px solid ${COLOR_STROKE_DIVIDER}`,
                borderRadius: "6px",
                height: "300px",
                overflowY: "auto"
              }}
            >
              {filteredInvestors.length === 0 ? (
                <Typography sx={{ p: "16px", color: COLOR_DISABLED, fontSize: "14px", textAlign: "center" }}>
                  {search ? "No matches." : "No investors available."}
                </Typography>
              ) : (
                filteredInvestors.map((investor) => {
                  const checked = String(investor.id) in allocations;
                  return (
                    <Stack
                      key={investor.id}
                      direction="row"
                      alignItems="center"
                      gap="12px"
                      onClick={() => toggleInvestor(investor.id)}
                      sx={{
                        padding: "8px 12px",
                        cursor: "pointer",
                        borderBottom: `1px solid ${COLOR_STROKE_DIVIDER}`,
                        "&:last-of-type": { borderBottom: "none" },
                        "&:hover": { backgroundColor: COLOR_BG_HOVER_SUBTLE }
                      }}
                    >
                      <Checkbox
                        checked={checked}
                        onClick={(e) => e.stopPropagation()}
                        onChange={() => toggleInvestor(investor.id)}
                        sx={{ padding: 0 }}
                      />
                      <Stack sx={{ flex: 1, minWidth: 0 }}>
                        <Typography
                          sx={{
                            color: COLOR_PRIMARY,
                            fontSize: "14px",
                            lineHeight: "20px",
                            fontWeight: 500,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap"
                          }}
                        >
                          {investor.name}
                        </Typography>
                      </Stack>
                      {checked && (
                        <Box sx={{ width: 150, flexShrink: 0 }} onClick={(e) => e.stopPropagation()}>
                          <NumberInput
                            name={`allocation-${investor.id}`}
                            value={allocations[String(investor.id)] ?? ""}
                            onChange={(e) => setAllocation(investor.id, (e.target as HTMLInputElement).value)}
                            size="small"
                            placeholder="Amount"
                            numberInputProps={{ decimalScale: 2, allowNegative: false }}
                            InputProps={{
                              endAdornment: (
                                <Typography sx={{ color: COLOR_SECONDARY, fontSize: "12px", ml: "4px" }}>
                                  {currency}
                                </Typography>
                              ),
                              sx: { "& input": { textAlign: "right", padding: "4px 6px" } }
                            }}
                            fullWidth
                          />
                        </Box>
                      )}
                    </Stack>
                  );
                })
              )}
            </Stack>
          </Stack>
        )}
      </Stack>

      <Stack sx={{ px: "24px", py: "16px", borderTop: `1px solid ${COLOR_STROKE_DIVIDER}`, gap: "8px" }}>
        {apiError && (
          <Typography sx={{ color: COLOR_ERROR, fontSize: "14px", lineHeight: "20px" }}>{apiError}</Typography>
        )}
        <Stack direction="row" alignItems="center" justifyContent="space-between" gap="12px">
          {step === 2 ? (
            <Button variant="text" onClick={handleBack} sx={{ textTransform: "none", color: COLOR_PRIMARY }}>
              Back
            </Button>
          ) : (
            <Box />
          )}
          <Stack direction="row" gap="12px">
            <Button
              variant="outlined"
              onClick={onCancel}
              sx={{ textTransform: "none", color: COLOR_PRIMARY, borderColor: COLOR_STROKE_IDLE }}
            >
              Cancel
            </Button>
            {step === 1 ? (
              <Button
                variant="contained"
                onClick={handleNext}
                disabled={!isStep1Valid}
                sx={{
                  textTransform: "none",
                  backgroundColor: COLOR_PRIMARY,
                  "&:hover": { backgroundColor: "#0d2c4d" }
                }}
              >
                Next
              </Button>
            ) : (
              <Button
                variant="contained"
                onClick={handleSave}
                disabled={isSaving || !allocationsValid}
                sx={{
                  textTransform: "none",
                  backgroundColor: COLOR_PRIMARY,
                  "&:hover": { backgroundColor: "#0d2c4d" }
                }}
              >
                {isSaving ? "Saving..." : "Save"}
              </Button>
            )}
          </Stack>
        </Stack>
      </Stack>
    </Stack>
  );
};
